/**
 * La gamme des packs : ce qui différencie réellement une offre de la suivante.
 *
 * Un pack porte un prix et un volume de tokens ; leur rapport — le prix au
 * token — est l'argument commercial du produit. Rien dans l'écran ne le disait :
 * quatre cartes identiques pour des offres allant de 10 000 € à 400 000 €.
 *
 * La gamme vendue est à prix strictement proportionnel : il n'y a donc pas de
 * remise à calculer, et ce module n'en calcule pas. Le jour où une grille
 * redevient dégressive, la comparaison se réécrira en dix lignes — la garder
 * en attendant reviendrait à porter du code qui ne modélise rien.
 *
 * Tout se calcule ici, en `BigInt`, et jamais en flottant. Un prix au token
 * calculé en `number` réintroduirait l'arrondi silencieux que le grand livre
 * s'interdit d'un bout à l'autre — sur une page de tarifs, c'est le pire
 * endroit pour l'admettre.
 */

import type { Pack } from "../api";
import { CENTIEMES_PAR_TOKEN, toBigInt } from "../format";

/** Ce qu'on sait dire d'un pack une fois qu'on le compare aux autres. */
export type RangGamme = {
  /** Position dans la gamme, de 0 (le plus petit) à `total - 1`. */
  rang: number;
  total: number;
  /** Prix au token, en centimes. Entier : voir la note sur l'arrondi. */
  prixUnitaireCents: bigint;
};

/**
 * Prix au token, en centimes, arrondi au centime le plus proche.
 *
 * Le volume du pack est en centièmes de token : le prix d'UN token vaut donc
 * `prix × 100 / centièmes`. L'arrondi est fait à la moitié supérieure et en
 * entiers : `(a + b/2) / b`. On affiche un prix unitaire, donc le centime est
 * la précision juste — mais c'est un affichage, et le montant qui fait foi
 * reste le prix du pack, jamais ce rapport.
 */
export function prixAuToken(pack: Pack): bigint {
  const prix = toBigInt(pack.price_cents);
  const centiemes = toBigInt(pack.tokens);

  if (centiemes <= 0n) {
    return 0n;
  }

  return (prix * CENTIEMES_PAR_TOKEN + centiemes / 2n) / centiemes;
}

/**
 * Situe chaque pack dans la gamme.
 *
 * La référence est le pack au prix au token le PLUS ÉLEVÉ — celui qui n'offre
 * aucune remise. C'est par rapport à lui qu'une remise a un sens commercial :
 * annoncer « −25 % » par rapport au moins cher n'aurait aucune signification.
 *
 * L'ordre reçu est conservé : le serveur trie par prix croissant, et
 * réordonner ici ferait diverger l'écran du contrat.
 */
export function situerGamme(packs: readonly Pack[]): Map<string, RangGamme> {
  const situation = new Map<string, RangGamme>();

  if (packs.length === 0) {
    return situation;
  }

  const unitaires = packs.map(prixAuToken);

  packs.forEach((pack, i) => {
    situation.set(pack.id, {
      rang: i,
      total: packs.length,
      prixUnitaireCents: unitaires[i],
    });
  });

  return situation;
}
